<template>
  <div class="space-y-3">
    <div class="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-gray-700">
      <div class="flex items-start gap-2">
        <i class="pi pi-info-circle mt-0.5 text-primary"></i>
        <div>
          <p class="font-semibold text-gray-800">Programme modules</p>
          <p class="mt-1 text-xs text-gray-600">
            Modules are the time-bound blocks a modular or residence programme teaches in
            (e.g. "Module 1 (January)" or "Feb Residency"). They belong to a session and run
            in sequence; the label you enter is what appears on results and transcripts.
            Pin a module to a semester to restrict registration to that calendar. Reporting is configured separately under Programme → Module Results.
          </p>
          <p class="mt-1.5 text-xs text-gray-600">
            <strong>How registration opens and closes:</strong> students see and register a module's
            courses only while the module is <strong>Open</strong> — that is, Active <em>and</em>
            today falls between its start and end dates. Deactivate a module (or let its dates pass)
            and its courses disappear from student registration. Courses must be assigned to an open module
            in the current semester to be available. Staff can push through a blocked registration with the
            override options on the admin registration screens.
          </p>
          <p class="mt-1.5 text-xs text-gray-500">
            <strong>Only using regular semester programmes?</strong> Skip this step entirely —
            modules have no effect on regular delivery, and nothing here needs to be set up.
          </p>
        </div>
      </div>
    </div>

    <details class="rounded-lg border p-3 text-sm text-gray-600">
      <summary class="cursor-pointer font-medium text-gray-800">Setup order and year-wide availability</summary>
      <ol class="mt-2 list-decimal pl-5 space-y-1">
        <li>Select the programme and session, then create modules with their dates.</li>
        <li>In Curriculum, assign the appropriate level/year courses to each module.</li>
        <li>Activate each module when its course assignments and dates are ready.</li>
      </ol>
      <p class="mt-2">
        For courses offered across a year, use the intended curriculum level/year and Any Semester (0), leave
        the module semester pin blank, and set its open dates. Year-based progression alone does not remove
        semester restrictions.
      </p>
      <p class="mt-2">
        Closing a module stops new student changes to its courses; earlier registrations remain. A module
        cannot be deleted while curriculum or registered courses reference it.
      </p>
    </details>

    <!-- Scope selectors -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
      <div class="flex flex-col gap-0.5">
        <label class="text-xs font-medium text-gray-600">Programme *</label>
        <ProgrammeSelect v-model="programmeId" data-testid="module-programme-select" />
      </div>
      <div class="flex flex-col gap-0.5">
        <label class="text-xs font-medium text-gray-600">Session *</label>
        <Select
          v-model="sessionId"
          :options="sessionOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select Session"
          class="w-full"
          filter
          showClear
          :loading="loadingSessions"
          data-testid="module-session-select"
        />
      </div>
    </div>

    <div
      v-if="programmeId && !selectedProgrammeUsesModules"
      class="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-800"
      data-testid="module-regular-warning"
    >
      This programme uses <strong>regular</strong> (semester-based) delivery, so modules will not
      affect it. Change its delivery mode to Modular or Residence in the Programmes step first.
    </div>

    <template v-if="programmeId && sessionId">
      <CloneModuleTimetable :programme-id="programmeId" :session-id="sessionId" :sessions="sessionOptions" :modules="modules" />
      <!-- Create form -->
      <form class="border border-gray-200 rounded-lg p-3 space-y-2.5" @submit.prevent="handleCreate">
        <p class="text-xs font-semibold text-gray-700">Add a module</p>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2.5">
          <div class="flex flex-col gap-0.5 lg:col-span-2">
            <label class="text-xs font-medium text-gray-600">Label *</label>
            <InputText
              v-model="form.name"
              placeholder='e.g. "Module 1 (January)"'
              data-testid="module-name-input"
            />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Starts *</label>
            <Calendar
              v-model="form.started_at"
              dateFormat="yy-mm-dd"
              placeholder="Start date"
              showIcon
              data-testid="module-start-input"
            />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Ends *</label>
            <Calendar
              v-model="form.ended_at"
              dateFormat="yy-mm-dd"
              placeholder="End date"
              showIcon
              data-testid="module-end-input"
            />
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Sequence</label>
            <InputNumber
              v-model="form.sequence"
              placeholder="Auto"
              :min="1"
              class="w-full"
              inputClass="w-full"
              data-testid="module-sequence-input"
            />
          </div>
          <div class="flex flex-col gap-0.5 lg:col-span-2">
            <label class="text-xs font-medium text-gray-600">Pin to semester (optional)</label>
            <Select
              v-model="form.academic_calendar_id"
              :options="calendarOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Any semester in this session"
              class="w-full"
              showClear
              data-testid="module-calendar-select"
              aria-describedby="module-pin-help"
            />
            <small id="module-pin-help" class="text-xs text-gray-600">
              Select a semester to restrict this module to it. Leave blank to allow any semester in this
              session while the module is open. Curriculum level/year and semester restrictions still apply.
            </small>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-700" for="form-module-fee-template">Registration fee item</label>
            <Select
              id="form-module-fee-template"
              v-model="form.registration_fee_item_template_id"
              :options="templateOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="No registration fee"
              class="w-full"
              filter
              showClear
              :loading="loadingTemplates"
              data-testid="module-fee-template-select"
              aria-describedby="module-fee-template-help"
            />
            <small id="module-fee-template-help" class="text-xs text-gray-600">
              Pick a Fee Item Template from the Billing catalog. Leave blank to charge no registration fee.
              Manage templates under Billing → Fee Items.
            </small>
            <small v-if="getErrorMessage('registration_fee_item_template_id')" class="text-xs text-red-500">{{ getErrorMessage('registration_fee_item_template_id') }}</small>
          </div>
          <div class="flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-700" for="form-module-retake-fee-template">Retake fee item</label>
            <Select
              id="form-module-retake-fee-template"
              v-model="form.retake_fee_item_template_id"
              :options="templateOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Retake is free"
              class="w-full"
              filter
              showClear
              :loading="loadingTemplates"
              data-testid="module-retake-fee-template-select"
              aria-describedby="module-retake-fee-template-help"
            />
            <small id="module-retake-fee-template-help" class="text-xs text-gray-600">
              Applied to 2nd and later attempts. Leave blank to make retakes free — the registration fee is
              never re-used as a fallback.
            </small>
            <small v-if="getErrorMessage('retake_fee_item_template_id')" class="text-xs text-red-500">{{ getErrorMessage('retake_fee_item_template_id') }}</small>
          </div>
        </div>
        <RegistrationUnitLimitFields v-model:minimum="form.min_units_per_registration"
          v-model:maximum="form.max_units_per_registration" scope="Module" id-prefix="module-create"
          :minimum-error="getErrorMessage('min_units_per_registration')"
          :maximum-error="getErrorMessage('max_units_per_registration')" />
        <p v-if="dateError" class="text-xs text-red-500" data-testid="module-date-error">{{ dateError }}</p>
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="form.is_active"
              :binary="true"
              inputId="moduleActivateImmediately"
              data-testid="module-active-checkbox"
            />
            <label for="moduleActivateImmediately" class="text-xs text-gray-600">
              Activate immediately — students can register its courses while the dates are in range.
              Inactive modules stay hidden from students until activated.
            </label>
          </div>
          <Button
            type="submit"
            label="Add Module"
            icon="pi pi-plus"
            size="small"
            :loading="creatingProgrammeModule"
            :disabled="!canCreate"
            data-testid="module-add-btn"
          />
        </div>
      </form>

      <!-- Modules table -->
      <DataTable :value="modules" :loading="loadingModules" size="small" stripedRows>
        <template #empty>
          <p class="text-xs text-gray-500 py-2">No modules yet for this programme and session.</p>
        </template>
        <Column field="sequence" header="#" style="width: 3rem" />
        <Column field="name" header="Label" />
        <Column field="started_at" header="Starts" />
        <Column field="ended_at" header="Ends" />
        <Column header="Semester pin">
          <template #body="{ data }">
            <span :data-testid="`module-pin-${data.id}`">{{ calendarLabel(data.academic_calendar_id) }}</span>
          </template>
        </Column>
        <Column header="Status" style="width: 9rem">
          <template #body="{ data }">
            <Tag
              :value="statusOf(data).label"
              :severity="statusOf(data).severity"
              :data-testid="`module-status-${data.id}`"
            />
          </template>
        </Column>
        <Column header="Active" style="width: 5rem">
          <template #body="{ data }">
            <InputSwitch
              :modelValue="data.is_active"
              :disabled="togglingProgrammeModule"
              :data-testid="`module-toggle-${data.id}`"
              @update:modelValue="handleToggle(data, $event)"
            />
          </template>
        </Column>
        <Column header="" style="width: 12rem">
          <template #body="{ data }">
            <div class="flex gap-1 justify-end">
              <Button v-if="selectedProgrammeUsesModules" label="Electives" text size="small"
                @click="electiveModule = data; showElectiveOfferings = true" />
              <Button
                icon="pi pi-pencil"
                text
                size="small"
                :data-testid="`module-edit-${data.id}`"
                @click="openEdit(data)"
              />
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                size="small"
                :loading="deletingProgrammeModule && deletingId === data.id"
                :data-testid="`module-delete-${data.id}`"
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </template>

    <Dialog v-model:visible="showElectiveOfferings" header="Additional Elective Offerings" modal class="w-full max-w-3xl">
      <ModuleElectiveOfferings v-if="electiveModule && showElectiveOfferings" :key="electiveModule.id" :module="electiveModule" :calendars="calendarOptions" />
    </Dialog>

    <!-- Edit dialog -->
    <Dialog v-model:visible="showEdit" header="Edit Module" modal class="w-full max-w-lg">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        <div class="flex flex-col gap-0.5 md:col-span-2">
          <label class="text-xs font-medium text-gray-700">Label *</label>
          <InputText v-model="editForm.name" data-testid="module-edit-name-input" />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Starts *</label>
          <Calendar
            v-model="editForm.started_at"
            dateFormat="yy-mm-dd"
            showIcon
            data-testid="module-edit-start-input"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Ends *</label>
          <Calendar
            v-model="editForm.ended_at"
            dateFormat="yy-mm-dd"
            showIcon
            data-testid="module-edit-end-input"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Sequence</label>
          <InputNumber
            v-model="editForm.sequence"
            :min="1"
            class="w-full"
            inputClass="w-full"
            data-testid="module-edit-sequence-input"
          />
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Pin to semester</label>
          <Select
            v-model="editForm.academic_calendar_id"
            :options="calendarOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Any semester in this session"
            class="w-full"
            showClear
            data-testid="module-edit-calendar-select"
            aria-describedby="module-edit-pin-help"
            @change="pinTouched = true"
          />
          <small id="module-edit-pin-help" class="text-xs text-gray-600">
            Leaving this blank allows any semester in this session, within the module dates. It does not
            remove curriculum level/year or semester restrictions.
          </small>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5 mt-2">
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700" for="editForm-module-fee-template">Registration fee item</label>
          <Select
            id="editForm-module-fee-template"
            v-model="editForm.registration_fee_item_template_id"
            :options="templateOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="No registration fee"
            class="w-full"
            filter
            showClear
            :loading="loadingTemplates"
            data-testid="module-edit-fee-template-select"
          />
          <small v-if="getErrorMessage('registration_fee_item_template_id')" class="text-xs text-red-500">{{ getErrorMessage('registration_fee_item_template_id') }}</small>
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700" for="editForm-module-retake-fee-template">Retake fee item</label>
          <Select
            id="editForm-module-retake-fee-template"
            v-model="editForm.retake_fee_item_template_id"
            :options="templateOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Retake is free"
            class="w-full"
            filter
            showClear
            :loading="loadingTemplates"
            data-testid="module-edit-retake-fee-template-select"
          />
          <small v-if="getErrorMessage('retake_fee_item_template_id')" class="text-xs text-red-500">{{ getErrorMessage('retake_fee_item_template_id') }}</small>
        </div>
      </div>
        <RegistrationUnitLimitFields v-model:minimum="editForm.min_units_per_registration"
          v-model:maximum="editForm.max_units_per_registration" scope="Module" id-prefix="module-edit"
          :minimum-error="getErrorMessage('min_units_per_registration')"
          :maximum-error="getErrorMessage('max_units_per_registration')" />
        <p v-if="editDateError" class="text-xs text-red-500 mt-2" data-testid="module-edit-date-error">{{ editDateError }}</p>
      <template #footer>
        <Button label="Cancel" text size="small" @click="showEdit = false" />
        <Button
          label="Save"
          icon="pi pi-check"
          size="small"
          :loading="updatingProgrammeModule"
          :disabled="!canSaveEdit"
          data-testid="module-edit-save-btn"
          @click="submitEdit"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import ModuleElectiveOfferings from './ModuleElectiveOfferings.vue';
import RegistrationUnitLimitFields from '@/components/common/RegistrationUnitLimitFields.vue';
import { registrationUnitLimitError } from '@/utils/registrationUnitLimits';
import { useFormErrors } from '@/composables/useFormErrors';
import { useBillItemTemplates } from '@/composables/billing/useBillItemTemplates';
import CloneModuleTimetable from './CloneModuleTimetable.vue';
import { useConfirm } from 'primevue/useconfirm';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useProgrammeModules } from '@/composables/queries/useProgrammeModules';
import { useSessionsWithCalendarsQuery } from '@/types/vue-apollo-helpers';
import { ProgrammeDeliveryModeEnum } from '@/types/composition-functions';
import type { ProgrammeModuleType } from '@/types/composition-functions';
import { calendarOptionLabel, calendarPinLabel, sessionOptionLabel } from '@/utils/moduleCalendarLabel';

const confirm = useConfirm();
const {
  createProgrammeModule,
  updateProgrammeModule,
  deleteProgrammeModule,
  toggleProgrammeModule,
  creatingProgrammeModule,
  updatingProgrammeModule,
  deletingProgrammeModule,
  togglingProgrammeModule,
} = useTertiaryMutations();

// ─── Scope ───────────────────────────────────────────────────────
const electiveModule = ref<{ id: string; name: string; academic_calendar_id?: string | null } | null>(null);
const showElectiveOfferings = ref(false);
const programmeId = ref<string | ''>('');
const sessionId = ref<string | null>(null);

const { programmes } = useProgrammes({ autoFetch: true, first: 200 });
const selectedProgrammeUsesModules = computed(() => {
  const programme = programmes.value?.find(p => p.id === programmeId.value);
  if (!programme) return true; // unknown yet — don't warn prematurely
  return programme.delivery_mode === ProgrammeDeliveryModeEnum.Modular
    || programme.delivery_mode === ProgrammeDeliveryModeEnum.Residence;
});

const { result: sessionsResult, loading: loadingSessions } = useSessionsWithCalendarsQuery();
const sessions = computed(() => sessionsResult.value?.schoolSessions ?? []);
const sessionOptions = computed(() =>
  sessions.value.map(s => ({ label: sessionOptionLabel(s), value: s.id })),
);
const calendarOptions = computed(() => {
  const session = sessions.value.find(s => s.id === sessionId.value);
  return (session?.academicCalendars ?? []).map(c => ({
    label: calendarOptionLabel(c),
    value: c.id,
  }));
});

function calendarLabel(calendarId: string | null | undefined): string {
  return calendarPinLabel(sessions.value, calendarId, loadingSessions.value);
}

// ─── Module list ─────────────────────────────────────────────────
const moduleVariables = computed(() => ({
  programme_id: programmeId.value ?? '',
  session_id: sessionId.value ?? undefined,
}));
const { modules, loadingModules, refetchModules } = useProgrammeModules(moduleVariables);

// ─── Create ──────────────────────────────────────────────────────
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

// Fee Item Templates — module fees route through the Billing catalog so bills
// carry GL account, settlement account, and category like every other invoice.
const { templates, loading: loadingTemplates, load: loadTemplates } = useBillItemTemplates({ autoFetch: true });
void loadTemplates;
const templateOptions = computed(() =>
  templates.value.map(t => ({
    label: `${t.name} — ₦${Number(t.default_amount ?? 0).toLocaleString()}`,
    value: t.id,
  })),
);

const form = reactive({
  registration_fee_item_template_id: null as string | null,
  retake_fee_item_template_id: null as string | null,
  min_units_per_registration: null as number | null,
  max_units_per_registration: null as number | null,
  name: '',
  sequence: null as number | null,
  started_at: undefined as Date | undefined,
  ended_at: undefined as Date | undefined,
  academic_calendar_id: null as string | null,
  // Default ON (unlike the backend's false): open/closed then derives from the
  // date window, per the agreed auto-floating; deactivation is the override.
  is_active: true,
});

function formatDateStr(date: Date | string | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// A bare 'YYYY-MM-DD' parses as UTC midnight, which formatDateStr would then
// read back a day early in UTC-negative timezones — parse as local instead.
function parseDateOnly(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`);
}

const dateError = computed(() => {
  if (form.started_at && form.ended_at
    && new Date(form.ended_at).getTime() < new Date(form.started_at).getTime()) {
    return 'The module end date must be on or after the start date.';
  }
  return '';
});

const canCreate = computed(() =>
  !!programmeId.value
  && !!sessionId.value
  && !!form.name.trim()
  && !!form.started_at
  && !!form.ended_at
  && !dateError.value && !registrationUnitLimitError(form),
);

async function handleCreate() {
  if (!canCreate.value) return;
  clearErrors();
  try {
    await createProgrammeModule({
      programme_id: programmeId.value!,
      session_id: sessionId.value!,
      academic_calendar_id: form.academic_calendar_id || undefined,
      name: form.name.trim(),
      sequence: form.sequence ?? undefined,
      started_at: formatDateStr(form.started_at),
      ended_at: formatDateStr(form.ended_at),
      is_active: form.is_active,
      registration_fee_item_template_id: form.registration_fee_item_template_id,
      retake_fee_item_template_id: form.retake_fee_item_template_id,
      min_units_per_registration: form.min_units_per_registration,
      max_units_per_registration: form.max_units_per_registration,
    });
  } catch (err) {
    handleServerErrors(err);
    // The mutation wrapper already toasted; keep the form intact for a retry.
    return;
  }
  form.registration_fee_item_template_id = null;
  form.retake_fee_item_template_id = null;
  form.min_units_per_registration = null;
  form.max_units_per_registration = null;
  form.name = '';
  form.sequence = null;
  form.started_at = undefined;
  form.ended_at = undefined;
  form.academic_calendar_id = null;
  form.is_active = true;
  await refetchModules();
}

// ─── Status / toggle ─────────────────────────────────────────────
function statusOf(module: ProgrammeModuleType): { label: string; severity: string } {
  if (module.is_open) return { label: 'Open', severity: 'success' };
  if (!module.is_active) return { label: 'Inactive', severity: 'secondary' };
  const today = formatDateStr(new Date());
  if (module.started_at && today < String(module.started_at)) {
    return { label: 'Scheduled', severity: 'info' };
  }
  return { label: 'Ended', severity: 'warn' };
}

async function handleToggle(module: ProgrammeModuleType, is_active: boolean) {
  try {
    await toggleProgrammeModule(module.id, is_active);
  } catch {
    // The switch is rendered from cache; refetch to snap it back to the truth.
    await refetchModules();
  }
}

// ─── Edit ────────────────────────────────────────────────────────
const showEdit = ref(false);
const editingId = ref<string | null>(null);
// PrimeVue Select can emit an initial update:modelValue when its options list
// hydrates, which fools "did the value change?" checks. Only send the pin when
// the user actually opens the dropdown and picks something.
const pinTouched = ref(false);
const editForm = reactive({
  registration_fee_item_template_id: null as string | null,
  retake_fee_item_template_id: null as string | null,
  min_units_per_registration: null as number | null,
  max_units_per_registration: null as number | null,
  name: '',
  sequence: null as number | null,
  started_at: undefined as Date | undefined,
  ended_at: undefined as Date | undefined,
  academic_calendar_id: null as string | null,
});

const editDateError = computed(() => {
  if (editForm.started_at && editForm.ended_at
    && new Date(editForm.ended_at).getTime() < new Date(editForm.started_at).getTime()) {
    return 'The module end date must be on or after the start date.';
  }
  return '';
});

const canSaveEdit = computed(() =>
  !!editForm.name.trim() && !!editForm.started_at && !!editForm.ended_at && !editDateError.value && !registrationUnitLimitError(editForm),
);

function openEdit(module: ProgrammeModuleType) {
  clearErrors();
  // Always resolve to the freshest row from the query result — the DataTable
  // may pass a memoized reference from before the last refetch. Falls back to
  // the row itself if the modules list is somehow empty (initial render).
  const fresh = modules.value.find(m => m.id === module.id) ?? module;
  pinTouched.value = false;
  editForm.registration_fee_item_template_id = fresh.registration_fee_item_template_id ?? null;
  editForm.retake_fee_item_template_id = fresh.retake_fee_item_template_id ?? null;
  editForm.min_units_per_registration = fresh.min_units_per_registration ?? null;
  editForm.max_units_per_registration = fresh.max_units_per_registration ?? null;
  editingId.value = fresh.id;
  editForm.name = fresh.name;
  editForm.sequence = fresh.sequence;
  editForm.started_at = fresh.started_at ? parseDateOnly(String(fresh.started_at)) : undefined;
  editForm.ended_at = fresh.ended_at ? parseDateOnly(String(fresh.ended_at)) : undefined;
  editForm.academic_calendar_id = fresh.academic_calendar_id ?? null;
  showEdit.value = true;
}

async function submitEdit() {
  if (!editingId.value || !canSaveEdit.value) return;
  clearErrors();
  // Only send academic_calendar_id when the admin actually interacted with the
  // Select. PrimeVue's Select fires a spurious update:modelValue on mount that
  // makes editForm.academic_calendar_id look changed even when the user did
  // not touch it, tripping the backend's "clone don't re-pin" guard.
  const payload: Record<string, unknown> = {
    name: editForm.name.trim(),
    registration_fee_item_template_id: editForm.registration_fee_item_template_id,
    retake_fee_item_template_id: editForm.retake_fee_item_template_id,
    min_units_per_registration: editForm.min_units_per_registration,
    max_units_per_registration: editForm.max_units_per_registration,
    sequence: editForm.sequence ?? undefined,
    started_at: formatDateStr(editForm.started_at),
    ended_at: formatDateStr(editForm.ended_at),
  };
  if (pinTouched.value) {
    payload.academic_calendar_id = editForm.academic_calendar_id;
  }
  let result;
  try {
    result = await updateProgrammeModule(editingId.value, payload);
  } catch (err) {
    handleServerErrors(err);
    // Toasted by the mutation wrapper; keep the dialog open for a retry.
    return;
  }
  if (result) {
    showEdit.value = false;
    editingId.value = null;
    await refetchModules();
  }
}

// ─── Delete ──────────────────────────────────────────────────────
const deletingId = ref<string | null>(null);

function confirmDelete(module: ProgrammeModuleType) {
  confirm.require({
    message: `Delete "${module.name}"? Courses assigned to it must be unassigned first.`,
    header: 'Delete Module',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      deletingId.value = module.id;
      try {
        const ok = await deleteProgrammeModule(module.id);
        if (ok) await refetchModules();
      } catch {
        // Toasted by the mutation wrapper; nothing to roll back.
      } finally {
        deletingId.value = null;
      }
    },
  });
}
</script>
